import { DonutChart } from "@mantine/charts";
import {
    Badge,
    Button,
    ColorSwatch,
    Fieldset,
    Group,
    MantineColor,
    Radio,
    rem,
    Skeleton,
    Stack,
    Table,
    Text,
    Title,
    useMantineTheme,
} from "@mantine/core";
import { useField } from "@mantine/form";
import { useInterval } from "@mantine/hooks";
import { queryOptions, useInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useEffect } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { LoadMoreTrigger } from "shared/components/global/LoadMoreTrigger";
import { Section, SectionProvider } from "shared/components/global/Section";
import { getRelativeTime } from "shared/components/RelativeTime";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { usePermissionCheck } from "shared/stores/oidc";
import { useAside } from "shared/utils/aside";
import { voiding } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { createOffsetBasedGqlQueryOptions } from "shared/utils/query";
import { zDate } from "shared/utils/zod";
import { graphql } from "src/gql";
import { MessageStatus, Permission } from "src/gql/graphql";

export const MessagePoll = (props: { msgId: string }) => {
    const { data } = useSuspenseQuery(queries.content(props.msgId));
    const selected = useField<string | null>({ initialValue: data?.poll?.options?.find((it) => it.isSelected)?.id ?? null });
    const submitResponse = useGraphqlMutation({
        document: muts.submitResponse,
        onError: () => notify.show.info({ message: "Only recipients of this message may submit responses." }),
    });
    const aside = useAside();

    const updater = useInterval(() => {
        qk.invalidate("messages", "poll", "content").catch(notify.catch);
        qk.invalidate("messages", "poll", "insights").catch(notify.catch);
    }, 5000);

    useEffect(() => {
        updater.start();
        return () => updater.stop();
    }, []);

    useEffect(() => {
        selected.setValue(data.poll?.options?.find((it) => it.isSelected)?.id ?? null);
    }, [data.poll?.options]);

    const canViewResponses = usePermissionCheck(Permission.GroupAnalyticsView, data.sender.group.id);

    if (!data.poll) return null;

    return (
        <Content paper>
            <Stack>
                <Group justify="space-between">
                    <Title order={3}>{data?.poll?.content}</Title>
                    {canViewResponses && (
                        <Button
                            variant="default"
                            onClick={() =>
                                aside.toggle({
                                    label: <Title order={3}>Poll Responses</Title>,
                                    component: (
                                        <Suspense fallback={<Skeleton height="64px" w="100%" />}>
                                            <PollInsights msgId={props.msgId} />
                                        </Suspense>
                                    ),
                                })
                            }
                        >
                            Responses
                        </Button>
                    )}
                </Group>

                <Stack>
                    {data?.poll?.options?.map((option) => (
                        <Radio
                            key={option.id}
                            checked={option.id == selected.getValue()}
                            label={option.content}
                            onChange={(e) => {
                                if (e.target.checked) selected.setValue(option.id);
                            }}
                        />
                    ))}

                    <Button
                        disabled={selected.getValue() == (data?.poll?.id ?? null)}
                        loading={submitResponse.isPending}
                        onClick={voiding(async () => {
                            const pollId = data?.poll?.id;
                            if (!pollId) return;

                            const responseId = selected.getValue();
                            if (!responseId) return;

                            await submitResponse.mutateAsync({ pollId, responseId });
                            await qk.invalidate("messages", "poll", "insights");
                            notify.show.success("Your response has been recorded.");
                        })}
                    >
                        Submit Answer
                    </Button>
                </Stack>
            </Stack>
        </Content>
    );
};

const PollInsights = (props: { msgId: string }) => {
    const { data } = useSuspenseQuery(queries.insights.counts(props.msgId));
    const theme = useMantineTheme();
    const colors = ["green", "red", "blue", "orange", "violet", "grape", "pink", "lime", "indigo", "cyan"] satisfies MantineColor[];
    const total = data?.reduce((sum, { count }) => sum + count, 0) ?? 0;

    return (
        <Stack gap="xl">
            <Fieldset legend="Breakdown" mt="md" p="sm">
                <Group justify="space-between" align="center">
                    <DonutChart
                        size={128}
                        withTooltip
                        tooltipDataSource="segment"
                        thickness={12}
                        strokeWidth={0}
                        data={
                            data?.map(({ count, option }, i) => ({
                                name: option.content,
                                color: colors[i],
                                value: count,
                            })) ?? []
                        }
                    />
                    <Stack gap={0}>
                        {data?.map(({ option }, i) => (
                            <Group key={option.id} wrap="nowrap" align="center">
                                <ColorSwatch color={theme.colors[colors[i]][6]} size={rem(16)} withShadow component="span" />
                                <Text size="sm">{option.content}</Text>
                            </Group>
                        ))}
                    </Stack>
                </Group>
            </Fieldset>

            <SectionProvider>
                {data?.map(({ count, option }) => (
                    <Section
                        key={option.id}
                        closed
                        label={
                            <Text>
                                <Text fw="bold" size="sm" component="span">
                                    {option.content}
                                </Text>
                                &nbsp;
                                <Text component="span" size="xs" style={{ textWrap: "nowrap" }}>
                                    {`(${Math.floor((count / total) * 100)}%, ${count} ${count == 1 ? "response" : "responses"})`}
                                </Text>
                            </Text>
                        }
                    >
                        {count == 0 ? (
                            <Text ta="center" c="dimmed" size="xs">
                                No Responses
                            </Text>
                        ) : (
                            <PollResponders msgId={props.msgId} optionId={option.id} />
                        )}
                    </Section>
                ))}
            </SectionProvider>
        </Stack>
    );
};

const PollResponders = (props: { msgId: string; optionId: string }) => {
    const { data } = useInfiniteQuery(queries.insights.responders(props.msgId, props.optionId));
    const responders = data?.pages.flat() ?? [];

    return (
        <Table>
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>User</Table.Th>
                    <Table.Th>Via</Table.Th>
                    <Table.Th>Time</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {responders.map((responder) => (
                    <Table.Tr key={responder.id}>
                        <Table.Td>{responder.user.name ?? "Unknown"}</Table.Td>
                        <Table.Td>{<Badge variant="default">{responder.method ?? "Web"}</Badge>}</Table.Td>
                        <Table.Td>{getRelativeTime(zDate.parse(responder.recordedAt))}</Table.Td>
                    </Table.Tr>
                ))}
            </Table.Tbody>
            <LoadMoreTrigger query={queries.insights.responders(props.msgId, props.optionId)} />
        </Table>
    );
};

const queries = {
    content: (msgId: string) =>
        queryOptions({
            queryKey: qk("messages", "poll", "content", msgId),
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query MessagePollContent($msgId: UUID!) {
                            message(id: $msgId) {
                                sender {
                                    group {
                                        id
                                    }
                                }
                                status
                                poll {
                                    id
                                    content
                                    options {
                                        id
                                        content
                                        isSelected
                                    }
                                }
                            }
                        }
                    `),
                    { msgId },
                ),
            select: (it) => ({ enabled: it.message.status == MessageStatus.Published, sender: it.message.sender, poll: it.message.poll }),
        }),
    insights: {
        counts: (msgId: string) =>
            queryOptions({
                queryKey: qk("messages", "poll", "insights", "count", msgId),
                queryFn: () =>
                    httpPostGraphql(
                        graphql(`
                            query MessagePollInsights($msgId: UUID!) {
                                message(id: $msgId) {
                                    poll {
                                        insights {
                                            counts {
                                                count
                                                option {
                                                    id
                                                    content
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        `),
                        { msgId },
                    ),
                select: (it) => it.message.poll?.insights.counts,
            }),
        responders: (msgId: string, optionId: string) =>
            createOffsetBasedGqlQueryOptions({
                queryKey: qk("messages", "poll", "insights", "option", msgId, optionId),
                document: graphql(`
                    query MessagePollResponders($msgId: UUID!, $optionId: UUID!, $limit: Int!, $offset: Int!) {
                        message(id: $msgId) {
                            poll {
                                insights {
                                    responses(limit: $limit, offset: $offset, optionId: $optionId) {
                                        id
                                        method
                                        recordedAt
                                        user {
                                            id
                                            name
                                        }
                                    }
                                }
                            }
                        }
                    }
                `),
                variables: { msgId, optionId },
                select: (it) => it.message.poll?.insights.responses ?? [],
            }),
    },
};

const muts = {
    submitResponse: graphql(`
        mutation MessagePollSubmitResponse($pollId: UUID!, $responseId: UUID!) {
            submitPollResponse(pollId: $pollId, optionId: $responseId) {
                id
            }
        }
    `),
};
